<template>
  <Popover
    ref="popover"
    data-testid="user-profile-popover"
    :clickable="false"
    :hide-delay="150"
    :hoverable="collapsed"
    :offset="8"
    :placement="placement"
    :show-delay="0"
    :z-index="110"
  >
    <template #trigger="{ active, open, toggle }">
      <button
        type="button"
        class="flex select-none items-center border-t bg-[--v3-theme-primarycolor] p-0 text-left text-[--v3-theme-primarytextcolor] transition-[background-color,border-color,height,width] duration-150 ease-out hover:bg-[--v3-theme-primarydarkercolor] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[--color-brand-forrest-green-80]"
        :class="collapsed
          ? 'h-[51px] w-[50px] justify-center overflow-hidden border-transparent'
          : 'h-[51px] w-[227px] overflow-hidden border-[--v3-theme-primarydarkercolor]'"
        :aria-label="collapsed ? 'Account menu' : undefined"
        aria-controls="user-profile-panel"
        :aria-expanded="active"
        @click="collapsed ? open() : toggle()"
      >
        <div
          class="avatar flex h-full items-center transition-[margin,width] duration-150 ease-out"
          :class="collapsed ? 'mx-0 w-[34px] justify-center overflow-visible' : 'mx-[15px] w-[197px] justify-between overflow-hidden'"
        >
          <div class="flex min-w-0 items-center">
            <Avatar
              :image="avatarImage"
              class="aspect-square size-[34px] shrink-0 overflow-hidden rounded-full"
              style="background-color: #ece9fc; color: #2a1261;"
              shape="circle"
            />
            <div
              class="overflow-hidden transition-[margin,max-width,opacity] duration-150 ease-out"
              :class="collapsed ? 'ml-0 max-w-0 opacity-0' : 'ml-[10px] max-w-[137px] opacity-100'"
            >
              <Skeleton v-if="isLoading" />
              <span
                v-else
                class="block truncate text-[14px] font-normal leading-[21px]"
                :class="{
                  'notranslate': !!name,
                }"
                :translate="name ? 'no' : 'yes'"
              >
                {{ name ?? 'Connect your shop' }}
              </span>
            </div>
          </div>
          <div
            class="flex size-[12px] shrink-0 items-center justify-center text-[--v3-blue-gray-bluegray-400] transition-[max-width,opacity] duration-150 ease-out"
            :class="collapsed ? 'max-w-0 opacity-0' : 'max-w-[12px] opacity-100'"
          >
            <Icon :name="active ? 'angle-down' : 'angle-up'" />
          </div>
        </div>
      </button>
    </template>
    <template #panel>
      <div id="user-profile-panel">
        <slot />
      </div>
    </template>
  </Popover>
</template>

<script setup lang='ts'>
import type { Placement } from '@floating-ui/vue';
import Avatar from 'primevue/avatar';
import Skeleton from 'primevue/skeleton';
import { computed, toRefs, useTemplateRef, watch } from 'vue';

import Icon from '@/common/components/atoms/Icon';
import Popover from '@/common/components/atoms/Popover';
import { assetUrl } from '@/helpers/assetUrl';

interface PopoverApi {
  close: () => void;
}

const props = withDefaults(defineProps<{
  avatar?: string
  collapsed?: boolean
  isLoading?: boolean
  name: string,
}>(), {
  avatar: "/img/avatar.png",
  collapsed: false,
  isLoading: false,
});
const { avatar, collapsed, isLoading, name } = toRefs(props);
const avatarImage = computed(() => assetUrl(avatar.value));

const popover = useTemplateRef<PopoverApi>('popover');
const placement = computed<Placement>(() => collapsed.value ? 'right-end' : 'top-start');

watch(collapsed, () => {
  popover.value?.close();
});
</script>
